import React, { memo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import App from '@/components/App';
import { TemplateParamsProvider } from '@/providers';

function Root({
  store,
  routerBasename,
  routerContext,
  routerLocation,
  env,
  helmetContext,
  templateParams,
}) {
  return (
    <ReduxProvider store={store}>
      {env === 'server' ? (
        <HelmetProvider context={helmetContext}>
          <StaticRouter
            location={routerLocation}
            context={routerContext}
            basename={routerBasename}
          >
            <TemplateParamsProvider {...templateParams}>
              <App />
            </TemplateParamsProvider>
          </StaticRouter>
        </HelmetProvider>
      ) : (
        <HelmetProvider>
          <BrowserRouter basename={routerBasename}>
            <TemplateParamsProvider {...templateParams}>
              <App />
            </TemplateParamsProvider>
          </BrowserRouter>
        </HelmetProvider>
      )}
    </ReduxProvider>
  );
}

Root.propTypes = {
  store: PropTypes.objectOf(PropTypes.any).isRequired,
  routerBasename: PropTypes.string.isRequired,
  routerContext({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'object')
    ) {
      return new Error('Please provide routerContext!');
    }

    return null;
  },
  routerLocation({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'string')
    ) {
      return new Error('Please provide routerLocation!');
    }

    return null;
  },
  env: PropTypes.oneOf(['client', 'server']).isRequired,
  helmetContext({ env, ...props }, propName) {
    const { [propName]: propValue } = props;

    if (
      env === 'server' &&
      (propValue === undefined || typeof propValue !== 'object')
    ) {
      return new Error('Please provide helmetContext!');
    }

    return null;
  },
  templateParams: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.any])),
};

Root.defaultProps = {
  routerContext: null,
  routerLocation: null,
  helmetContext: {},
  templateParams: {},
};

export default memo(Root);
